import type { FC, ReactNode } from 'react'

import { Clock3, MapPin } from 'lucide-react'
import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { Container } from '@/components/generals/container'
import { PageHero } from '@/components/generals/page-hero'
import { CtaBand } from '@/components/generals/cta-band'
import { SectionHeading } from '@/components/generals/section-heading'
import { Reveal } from '@/components/motion/reveal'
import { AreaExplorer } from '@/components/widgets/area-explorer'
import { AreaCard } from '@/components/widgets/area-card'
import { ZipChecker } from '@/components/widgets/zip-checker'
import { DISPATCH_NOTE, serviceAreas } from '@/constants/areas'
import { siteConfig, QUOTE_PATH } from '@/constants/site'

const byArrival = [...serviceAreas].sort((a, b) => a.arrivalMinutes - b.arrivalMinutes)

const ServiceAreasPage: FC = (): ReactNode => {
    return (
        <Layout>
            <Seo
                title='Service areas'
                description="Garage door repair and installation in eight Riverton metro towns, from Oak Hollow to Pine Ridge. Check your ZIP code and typical arrival time."
            />

            <PageHero
                eyebrow="Where we work"
                title="Eight towns, all reached from a single yard."
                accent={['single', 'yard.']}
                description="At 7 AM our trucks pull out of Foundry Street in Riverton. Type in your ZIP code to check coverage and the usual arrival time for your town."
                aside={(
                    <div className='rounded-[32px] border border-ink/[0.08] bg-paper p-6 shadow-[0_40px_90px_-50px_rgba(21,23,26,0.45)] sm:p-8'>
                        <ZipChecker />

                        <ul className='mt-4 space-y-3 border-t border-ink/[0.08] pt-6 text-[14.5px] text-slate'>
                            <li className='flex items-start gap-3'>
                                <MapPin className='mt-0.5 size-4 shrink-0 text-ember' aria-hidden='true' />
                                {siteConfig.address}
                            </li>

                            <li className='flex items-start gap-3'>
                                <Clock3 className='mt-0.5 size-4 shrink-0 text-ember' aria-hidden='true' />
                                {siteConfig.hours} · {siteConfig.emergencyHours}
                            </li>
                        </ul>
                    </div>
                )}
            />

            <section id='map' className='relative overflow-hidden bg-ink py-24 text-cream md:py-32'>
                <div aria-hidden='true' className='door-grooves pointer-events-none absolute inset-0 opacity-30' />

                <Container className='relative'>
                    <AreaExplorer>
                        <SectionHeading
                            layout='stack'
                            tone='light'
                            eyebrow="How soon we arrive"
                            title="The nearer the town, the shorter the wait."
                            description="Every town is placed on the map by its typical urgent arrival time, measured from our Riverton yard."
                        />
                    </AreaExplorer>
                </Container>
            </section>

            <section id='towns' className='py-24 md:py-32'>
                <Container>
                    <SectionHeading
                        eyebrow="All eight towns"
                        title="Find your town for arrival times and ZIP codes."
                        description={DISPATCH_NOTE}
                    />

                    <Reveal selector=':scope > li' as='ul' className='mt-14 grid gap-4 sm:grid-cols-2 lg:grid-cols-4'>
                        {byArrival.map(area => (
                            <li key={area.slug}>
                                <AreaCard area={area} />
                            </li>
                        ))}
                    </Reveal>
                </Container>
            </section>

            <CtaBand
                title="Inside our area? Let's price your door."
                body="Send a few details about the door. We'll text back a price range, usually within 30 minutes."
                primary={{ label: "Request a price", href: QUOTE_PATH }}
            />
        </Layout>
    )
}

export default ServiceAreasPage
